/**
 * Builder-specific selections viewer demo page
 * URL format: /demo/builders/[builder_name]/selections/[scene_id]?builderId=[builderId]&env=[rough|review|release]
 * Defaults to 'release' environment if not specified
 * @module
 */
import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import SelectionsViewer from "lib/components/selections/SelectionsViewer";
import styles from "lib/components/selections/SelectionsViewer.module.scss";
import useSelectionsViewer from "lib/components/selections/useSelectionsViewer";
import { useShowroomBuilderData } from "lib/components/showroom2/data/useShowroomPlanData";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import { useAppRouterParams } from "lib/routeHelpers";
import type { SelectionsViewerContextProps } from "lib/state/SelectionsViewerContext";
import SelectionsViewerContext from "lib/state/SelectionsViewerContext";
import { getSceneVersionManifestJson, getSelectionsDatabase } from "lib/storage/selectionsStorage";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { parseCompositorManifest } from "selections-compositor/state/selections";
import Loading from "ui/components/Loading";
import useAppContext from "ui/hooks/useAppContext";

const DEFAULT_ENVIRONMENT = "release";
const VALID_ENVIRONMENTS = ["release", "review", "rough"] as const;
type Environment = (typeof VALID_ENVIRONMENTS)[number];

function isValidEnvironment(env: string | undefined): env is Environment {
  return VALID_ENVIRONMENTS.includes(env as Environment);
}

function BuilderSelectionsViewerPage() {
  const { builderName, sceneId } = useAppRouterParams();
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  const { setUserNotification } = useAppContext();

  // Get environment from query param, default to prod
  const envParam = typeof searchParams.env === "string" ? searchParams.env : undefined;
  const environment: Environment = isValidEnvironment(envParam) ? envParam : DEFAULT_ENVIRONMENT;

  // Fetch builder data (only if builderId is present in URL)
  const builderData = useShowroomBuilderData();

  const {
    manifest,
    setManifest,
    activeSelections,
    scene,
    setScene,
    menuItems,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
  } = useSelectionsViewer({
    onActiveSelections: async () => {
      setTilesRefreshLastTimestamp(Date.now());
    },
  });

  const [tilesRefreshLastTimestamp, setTilesRefreshLastTimestamp] = useState<number>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    document.body.style.overflow = "hidden"; // Important to prevent scrolling on iPad
  }, []);

  useEffect(() => {
    async function fetchManifestForScene() {
      if (!sceneId || !builderName) {
        return;
      }

      setLoading(true);
      try {
        // Load the selections database to verify the scene exists
        const database = await getSelectionsDatabase();
        if (!database) {
          throw new Error("Could not load selections database");
        }

        // Generate sceneVersionId directly from sceneId and environment
        const sceneVersionId = `${sceneId}-${environment}`;

        // Load the manifest using the sceneVersionId
        const manifestJson = await getSceneVersionManifestJson(sceneVersionId);
        if (manifestJson) {
          const newManifest = parseCompositorManifest(manifestJson);
          setManifest(newManifest);
          setLoading(false);
        } else {
          throw new Error(`Manifest not found: ${sceneVersionId}`);
        }
      } catch (error) {
        console.error("Error loading selection manifest:", error);
        setUserNotification({
          message: `Cannot load scene "${sceneId}": ${error instanceof Error ? error.message : "Unknown error"}`,
          messageType: "error",
        });
        setLoading(false);
      }
    }

    void fetchManifestForScene();
  }, [sceneId, builderName, environment, setManifest, setUserNotification]);

  const selectionsViewerContext: SelectionsViewerContextProps = {
    manifest,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
    menuItems,
    activeSelections,
  };

  if (loading) {
    return <Loading />;
  }

  if (!manifest) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p>No selection manifest found for scene: {sceneId}</p>
        <p style={{ color: "#666", fontSize: "14px" }}>
          Builder: {builderName} | Scene: {sceneId} | Environment: {environment}
        </p>
      </div>
    );
  }

  return (
    <ShowroomProvider key={sceneId}>
      <Head>
        <title>{getPageTitle(`${builderData?.companyName || builderName} - Selections`)}</title>
      </Head>

      <SelectionsViewerContext.Provider value={selectionsViewerContext}>
        <div className={styles.selectionsPage}>
          <SelectionsViewer
            tilesRefreshLastTimestamp={tilesRefreshLastTimestamp}
            scene={scene}
            setScene={setScene}
            builderData={builderData ?? undefined}
            builderName={builderName}
          />
        </div>
      </SelectionsViewerContext.Provider>
    </ShowroomProvider>
  );
}

export const Route = createFileRoute("/demo/builders/$builder_name/selections/$scene_id/")({
  component: BuilderSelectionsViewerPage,
  staticData: { isPublicPage: true },
});
